import { MODULES, MODULE_LABELS, type Module } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Search, User, Users } from "lucide-react";
import { useState } from "react";
import { Link, Redirect, useLocation, useSearch } from "wouter";
import { Empty, Loading, PageHeader, Pager, Segmented, useServerPaged } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { useMemberNames } from "@/lib/members";
import { useToast } from "@/lib/use-toast";
import { hasPermission, type Me } from "@/lib/session";
import type { Client } from "@/lib/types";

const MODULE_SHORT: Record<Module, string> = { tax: "Tax", bookkeeping: "Accounting", payroll: "Payroll", advisory: "CFO & Advisory" };

export function ModuleBadges({ modules }: { modules?: Module[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(modules ?? []).map((m) => (
        <Badge key={m} variant="secondary" className="text-[11px]">
          {MODULE_SHORT[m]}
        </Badge>
      ))}
    </div>
  );
}

type IntakeTemplate = { id: string; name: string; kind: "organizer" | "intake"; isActive: boolean };

/** The intake form that fits the client type: the firm's form whose name mentions it, else the first active one. */
function intakeFor(templates: IntakeTemplate[] | undefined, kind: "individual" | "business") {
  const forms = (templates ?? []).filter((t) => t.kind === "intake" && t.isActive);
  return forms.find((t) => t.name.toLowerCase().includes(kind)) ?? forms[0];
}

function NewClientDialog() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"individual" | "business">("individual");
  const [displayName, setDisplayName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [modules, setModules] = useState<Module[]>([]);
  const [onboard, setOnboard] = useState(true);
  const [invite, setInvite] = useState(true);
  const [sendIntake, setSendIntake] = useState(true);
  const { data: templates } = useQuery<IntakeTemplate[]>({ queryKey: ["/api/organizer-templates"], enabled: open });
  const intake = intakeFor(templates, kind);
  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      const c = await api.send<Client>("POST", "/api/clients", {
        kind,
        displayName,
        email: email || undefined,
        phone: phone || undefined,
        mobilePhone: mobilePhone || undefined,
        modules,
        status: onboard ? "prospect" : "active",
      });
      // The client exists now; later steps report their own problems without losing it.
      const problems: string[] = [];
      if (onboard && invite && email) {
        await api
          .send("POST", `/api/clients/${c.id}/portal-users`, { email, name: kind === "individual" ? displayName : contactName || displayName })
          .catch((e) => problems.push(`Portal invitation: ${errorMessage(e)}`));
      }
      if (onboard && sendIntake && intake) {
        await api.send("POST", "/api/organizers", { clientId: c.id, templateId: intake.id }).catch((e) => problems.push(`Intake form: ${errorMessage(e)}`));
      }
      return { c, problems };
    },
    onSuccess: ({ c, problems }) => {
      qc.invalidateQueries({ queryKey: ["/api/clients"] });
      setOpen(false);
      if (problems.length) toast({ title: "Client created; some onboarding steps need attention", description: problems.join(" "), variant: "destructive" });
      // A new prospect opens on its onboarding page, where every step is laid out.
      navigate(onboard ? `/clients/onboarding/${c.id}` : `/clients/${c.id}`);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" /> New client
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            {(["individual", "business"] as const).map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => setKind(k)}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm ${kind === k ? "border-primary bg-primary/5 font-medium" : "border-border"}`}
              >
                {k === "individual" ? <User className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                {k === "individual" ? "Individual" : "Business"}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dn">{kind === "individual" ? "Full name" : "Business name"}</Label>
            <Input id="dn" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          {kind === "business" && (
            <div className="space-y-1.5">
              <Label htmlFor="cn">Primary contact name</Label>
              <Input id="cn" value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="em">Email</Label>
              <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ph">{kind === "business" ? "Office phone" : "Phone"}</Label>
              <Input id="ph" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mph">Mobile</Label>
              <Input id="mph" type="tel" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} />
            </div>
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">Engaged services</legend>
            <div className="grid grid-cols-2 gap-2">
              {MODULES.map((m) => (
                <label key={m} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={modules.includes(m)}
                    onChange={(e) => setModules((cur) => (e.target.checked ? [...cur, m] : cur.filter((x) => x !== m)))}
                  />
                  {MODULE_LABELS[m]}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3">
            <label className="flex items-start gap-2 text-sm font-medium">
              <input type="checkbox" className="mt-0.5" checked={onboard} onChange={(e) => setOnboard(e.target.checked)} />
              <span>
                Start onboarding
                <span className="block text-xs font-normal text-muted-foreground">The client is added as a prospect and tracked under Clients › Onboarding until onboarding is complete.</span>
              </span>
            </label>
            {onboard && (
              <div className="space-y-2 pl-6">
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={invite && !!email} disabled={!email} onChange={(e) => setInvite(e.target.checked)} />
                  <span>
                    Invite to the client portal
                    {!email && <span className="block text-xs text-muted-foreground">Enter an email address to invite the client.</span>}
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={sendIntake && !!intake} disabled={!intake} onChange={(e) => setSendIntake(e.target.checked)} />
                  <span>
                    Send the intake form{intake ? `: ${intake.name}` : ""}
                    {!intake && <span className="block text-xs text-muted-foreground">No active intake form. Create one under Clients › Intake Forms.</span>}
                  </span>
                </label>
              </div>
            )}
          </fieldset>
          {create.error && <p className="text-sm text-destructive">{errorMessage(create.error)}</p>}
          <Button type="submit" className="w-full" disabled={create.isPending || !displayName.trim()}>
            {onboard ? "Create client and start onboarding" : "Create client"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type View = "all" | "individual" | "business";
const VIEWS: Array<{ key: View; label: string; icon: typeof Users }> = [
  { key: "all", label: "All clients", icon: Users },
  { key: "individual", label: "Individuals", icon: User },
  { key: "business", label: "Businesses", icon: Building2 },
];

/** How many clients each tab holds. */
function useCounts() {
  const total = (kind?: string) => useQuery<{ total: number }>({ queryKey: ["/api/clients", `?limit=1${kind ? `&kind=${kind}` : ""}`] }).data?.total;
  return { all: total(), individual: total("individual"), business: total("business") };
}

export function ClientsPage({ me }: { me: Me }) {
  const [location, navigate] = useLocation();
  const requested = new URLSearchParams(useSearch()).get("view");
  const view = (requested === "individual" || requested === "business" ? requested : "all") as View;
  const setView = (v: View) => navigate(v === "all" ? location : `${location}?view=${v}`, { replace: true });
  const counts = useCounts();
  const [q, setQ] = useState("");
  const [module, setModule] = useState<Module | "">("");
  const { offset, size, paged } = useServerPaged(`${view}|${q.trim()}|${module}`);
  const params = new URLSearchParams({ offset: String(offset), limit: String(size) });
  if (q.trim()) params.set("q", q.trim());
  if (module) params.set("module", module);
  if (view === "individual" || view === "business") params.set("kind", view);
  const { data: page, isLoading } = useQuery<{ items: Client[]; total: number }>({ queryKey: ["/api/clients", `?${params}`], placeholderData: (prev) => prev, });
  const data = page?.items;
  const names = useMemberNames();

  return (
    <>
      <PageHeader title="Clients" description="All clients and the services they are engaged for." actions={hasPermission(me, "clients.write") && <NewClientDialog />} />
      <Segmented
        label="Client type"
        className="mb-5"
        value={view}
        onChange={setView}
        options={VIEWS.map((v) => ({
          value: v.key,
          count: counts[v.key],
          label: (
            <>
              <v.icon className="h-4 w-4" aria-hidden />
              {v.label}
            </>
          ),
        }))}
      />
      {requested === "onboarding" && <Redirect to="/clients/onboarding" replace />}
      <>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input aria-label="Search clients" placeholder="Search by name, email or phone" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select aria-label="Filter by service" value={module} onChange={(e) => setModule(e.target.value as Module | "")} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All services</option>
          {MODULES.map((m) => (
            <option key={m} value={m}>
              {MODULE_LABELS[m]}
            </option>
          ))}
        </select>
      </div>
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>{q || module ? "No clients match your search." : "No clients have been added."}</Empty>
      ) : (
        <div className="overflow-hidden rounded-lg border border-card-border bg-card">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="hidden w-48 px-4 py-3 font-medium md:table-cell">Email</th>
                <th className="hidden w-32 px-4 py-3 font-medium xl:table-cell">Phone</th>
                <th className="w-36 px-4 py-3 font-medium sm:w-40">Services</th>
                <th className="hidden w-32 px-4 py-3 font-medium lg:table-cell">Client manager</th>
                <th className="hidden w-20 px-4 py-3 font-medium sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((c) => (
                <tr key={c.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3">
                    <Link href={`/clients/${c.id}`} className="flex min-w-0 items-center gap-2 font-medium hover:underline" title={c.displayName}>
                      {c.kind === "business" ? <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" /> : <User className="h-4 w-4 shrink-0 text-muted-foreground" />}
                      <span className="truncate">{c.displayName}</span>
                    </Link>
                  </td>
                  <td className="hidden truncate px-4 py-3 text-muted-foreground md:table-cell" title={c.email ?? undefined}>{c.email ?? "—"}</td>
                  <td className="hidden whitespace-nowrap px-4 py-3 text-muted-foreground xl:table-cell">{c.mobilePhone ?? c.phone ?? "—"}</td>
                  <td className="px-4 py-3">
                    <ModuleBadges modules={c.modules} />
                  </td>
                  <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">{c.assignedUserId ? (names.get(c.assignedUserId) ?? "—") : "Unassigned"}</td>
                  <td className="hidden px-4 py-3 capitalize text-muted-foreground sm:table-cell">{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager paged={paged(page!.total)} noun="clients" className="px-4 pb-3" />
        </div>
      )}
      </>
    </>
  );
}
